import { Button } from "@/components/ui/button";
import {
  useGetAssistantId,
  useGetOpenaiId,
  useGetOutline,
  useGetThreadId,
  useProposalStore,
} from "@/stores/ProposalStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
const domain = import.meta.env.VITE_API_DOMAIN;

export function useStreamProposal(search_type, proposal_id) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const startStreaming = useProposalStore((state) => state.startStreaming);
  const appendProposalMarkdown = useProposalStore(
    (state) => state.appendProposalMarkdown
  );

  const thread_id = useGetThreadId(search_type, proposal_id);
  const assistant_id = useGetAssistantId(search_type, proposal_id);
  const outline = useGetOutline(search_type, proposal_id);
  const openai_ids = useGetOpenaiId(search_type, proposal_id);

  return useMutation({
    mutationKey: ["stream_proposal", proposal_id, search_type],
    mutationFn: async () => {
      startStreaming(search_type, proposal_id);
      const response = await fetch(`${domain}/proposal/stream_proposal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          proposal_id,
          search_type,
          thread_id,
          assistant_id,
          openai_ids,
          outline: JSON.stringify(outline),
        }),
      });
      if (!response.ok) {
        toast.error("Error generating proposal.");
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Return a new Promise and resolve when streaming is finished
      return new Promise((resolve, reject) => {
        function processText({ done, value }) {
          if (done) {
            resolve(); // Resolve promise when stream is done
            return;
          }
          const chunk = decoder.decode(value, { stream: true });
          appendProposalMarkdown(search_type, proposal_id, chunk);
          reader.read().then(processText).catch(reject); // Reject if any error in reading
        }

        reader.read().then(processText).catch(reject); // Start processing the stream
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries([
        "proposalStreaming",
        proposal_id,
        search_type,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ["proposalStreaming", proposal_id, search_type],
        (data) => ({
          status: "streaming",
          current_section: "",
          title: "",
          proposal_id,
          search_type,
          optimistic: true,
        })
      );

      queryClient.invalidateQueries(["activeStreamingProposals"]);
      queryClient.invalidateQueries([
        "editorContent",
        proposal_id,
        search_type,
      ]);
    },
    onSettled: async () => {
      await queryClient.cancelQueries([
        "proposalStreaming",
        proposal_id,
        search_type,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ["proposalStreaming", proposal_id, search_type],
        (data) => {
          if (!data) return data;
          return {
            ...data,
            status: "completed",
          };
        }
      );

      queryClient.invalidateQueries([
        "proposalContract",
        search_type,
        proposal_id,
      ]);
      queryClient.invalidateQueries([
        "editorContent",
        proposal_id,
        search_type,
      ]);
      queryClient.invalidateQueries(["proposalsContracts"]);
      queryClient.invalidateQueries(["activeStreamingProposals"]);
    },
    onSuccess: () => {
      toast.success(
        (t) => (
          <div className="flex justify-between items-center gap-x-8">
            <span>Proposal generated successfully</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="[&_svg]:size-3 [&_svg]:text-muted-foreground"
                size="sm"
                onClick={() => toast.dismiss(t.id)}
              >
                Dismiss
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  navigate(
                    `/contract/${search_type}/${proposal_id}/generate/editor`
                  );
                }}
              >
                View
              </Button>
            </div>
          </div>
        ),
        {
          duration: 5000,
        }
      );
    },
  });
}
